/**
 * Frontend entry for all shipping facts. Values live in
 * supabase/functions/_shared/shipping.ts so the checkout server uses the same numbers.
 */
export * from "../../supabase/functions/_shared/shipping";
import { DELIVERY_DAYS_MAX, DELIVERY_DAYS_MIN, FREE_SHIPPING_THRESHOLD_EUR, SHIPPING_COST_EUR, formatEuro } from "../../supabase/functions/_shared/shipping";
import { formatPriceEur } from "@/lib/shopView";
import { translations, type Language } from "@/lib/translations";

function days(language: string, key: "daysNom" | "daysDat"): string {
  const u = (translations[language as Language] ?? translations.de).shippingUnits[key];
  return u.replace("{min}", String(DELIVERY_DAYS_MIN)).replace("{max}", String(DELIVERY_DAYS_MAX));
}

/** Localized "3–5 Werktage" (nominative). */
export const deliveryTimeTextLocal = (language: string) => days(language, "daysNom");
/** Localized "3–5 Werktagen" (dative, after "in"). */
export const deliveryTimeDativeLocal = (language: string) => days(language, "daysDat");

/** Language-aware euro amount; whole amounts without decimals (50 €). */
export function shippingEuro(value: number, language: string): string {
  if (language === "de") return formatEuro(value);
  const s = formatPriceEur(value, language as "de");
  return Number.isInteger(value) ? s.replace(/[.,]00/, "") : s;
}

/** Placeholder values for shipping copy: {time}, {timeDat}, {cost}, {amount}. */
export function shippingVars(language: string): Record<string, string> {
  return {
    time: deliveryTimeTextLocal(language),
    timeDat: deliveryTimeDativeLocal(language),
    cost: shippingEuro(SHIPPING_COST_EUR, language),
    amount: shippingEuro(FREE_SHIPPING_THRESHOLD_EUR, language),
  };
}

export function fillShipping(text: string, language: string, extra: Record<string, string> = {}): string {
  const v = { ...shippingVars(language), ...extra };
  return text.replace(/\{(\w+)\}/g, (m, k: string) => v[k] ?? m);
}

/**
 * "Kaufbedingungen (Shop)" section (#kauf) on /agb. Only visible in the
 * development preview until the final texts are approved; never in the
 * published build while this stays tied to import.meta.env.DEV.
 */
export const SHOP_TERMS_SECTION_VISIBLE = import.meta.env.DEV;

/**
 * Checkout notice: link to the shop terms (/agb#kauf). Stays off until the
 * shop terms texts are approved and published.
 */
export const CHECKOUT_TERMS_LINK_ENABLED = false;
